import { Box, Button } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { Form, Formik } from "formik";
import {
  forwardRef,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import * as yup from "yup";
import useAPI from "../../../../../api/hooks/useAPI";
import { jiraKeys } from "../../../../../api/queryKeys";
import { useJiraQueries } from "../hooks/useJiraQueries";
import JiraCustomFields, {
  getCustomFieldsValidationSchema,
} from "./fields/JiraCustomFields";
import JiraDescription from "./fields/JiraDescription";
import JiraIncludeEntities from "./fields/JiraIncludeEntities";
import JiraPriority from "./fields/JiraPriority";
import JiraReporter from "./fields/JiraReporter";
import JiraRequestType from "./fields/JiraRequestType";
import JiraSelectedMisconfigurations from "./fields/JiraSelectedMisconfigurations";
import JiraSummary from "./fields/JiraSummary";
import { JIRA_FORM_WIDTH } from "./JiraFormSelector";

const JSMTicketForm = forwardRef(
  (
    { fullControl, selectedProject, issueAutoGeneratedFields, onSubmit },
    ref
  ) => {
    const { fetchJSMRequestTypes, fetchJSMRequestFields } = useAPI();
    const [selectedRequestType, setSelectedRequestType] = useState(null);
    const isPerMisconfiguration =
      issueAutoGeneratedFields?.selectedMisconfigurations !== null;
    const formikRef = useRef();
    const {
      assigneeOptions,
      reporterOptions,
      reporterOptionsIsSuccess,
      jsmIsAgent,
      jsmIsAgentIsSuccess,
    } = useJiraQueries(selectedProject);

    const formInitialValues = {
      projectName: selectedProject,
      requestType: null,
      issueSummary: issueAutoGeneratedFields?.summary || "",
      description: issueAutoGeneratedFields?.description || "",
      priority: null,
      reporter: null,
      selectedMisconfigurations:
        issueAutoGeneratedFields?.selectedMisconfigurations,
      includeEntities: !!issueAutoGeneratedFields?.entity_ids,
    };

    const { data: requestTypes, isSuccess: requestTypesIsSuccess } = useQuery({
      queryKey: jiraKeys.jiraRequestTypes(selectedProject?.key),
      queryFn: () => fetchJSMRequestTypes(selectedProject.key),
      select: (response) => response.data,

      enabled: !!selectedProject,
    });

    const {
      data: requestFields,
      isLoading: requestFieldsIsLoading,
      isSuccess: requestFieldsIsSuccess,
    } = useQuery({
      queryKey: jiraKeys.JSMRequestFields(
        selectedProject?.key,
        selectedRequestType?.id
      ),
      queryFn: () =>
        fetchJSMRequestFields(selectedProject.key, selectedRequestType?.id),
      select: (response) => response.data,

      enabled: !!selectedProject && !!selectedRequestType,
    });

    const customFields = useMemo(() => {
      if (!requestFieldsIsSuccess || !requestFields) return [];
      return requestFields.filter((field) =>
        field.key.startsWith("customfield_")
      );
    }, [requestFields, requestFieldsIsSuccess]);

    const priorityField = useMemo(() => {
      if (!requestFieldsIsSuccess || !requestFields) return null;
      return requestFields.find((field) => field.key === "priority");
    }, [requestFields, requestFieldsIsSuccess]);

    // Check if summary and description fields are in requestFields
    const showSummaryField = useMemo(() => {
      if (!requestFieldsIsSuccess || !requestFields) return true;
      return requestFields.some((field) => field.key === "summary");
    }, [requestFields, requestFieldsIsSuccess]);

    const showDescriptionField = useMemo(() => {
      if (!requestFieldsIsSuccess || !requestFields) return true;
      return requestFields.some((field) => field.key === "description");
    }, [requestFields, requestFieldsIsSuccess]);

    const showPriorityField = useMemo(() => {
      if (!requestFieldsIsSuccess || !requestFields) return false;
      return requestFields.some((field) => field.key === "priority");
    }, [requestFields, requestFieldsIsSuccess]);

    const isSummaryRequired = useMemo(() => {
      if (!requestFieldsIsSuccess || !requestFields) return true;
      const summaryField = requestFields.find(
        (field) => field.key === "summary"
      );
      return summaryField?.required ?? true;
    }, [requestFields, requestFieldsIsSuccess]);

    const isDescriptionRequired = useMemo(() => {
      if (!requestFieldsIsSuccess || !requestFields) return false;
      const descriptionField = requestFields.find(
        (field) => field.key === "description"
      );
      return descriptionField?.required ?? false;
    }, [requestFields, requestFieldsIsSuccess]);

    const formValidationSchema = useMemo(() => {
      const baseSchema = {
        projectName: yup
          .object()
          .nullable()
          .required("Project name is required"),
        requestType: yup
          .object()
          .nullable()
          .required("Request type is required"),
        reporter:
          jsmIsAgentIsSuccess && jsmIsAgent
            ? yup.object().nullable().required("Reporter is required")
            : yup.object().nullable(),
      };

      // Add summary validation if required
      if (showSummaryField) {
        baseSchema.issueSummary = isSummaryRequired
          ? yup.string().required("Issue summary is required")
          : yup.string();
      }

      // Add description validation if required
      if (showDescriptionField && isDescriptionRequired) {
        baseSchema.description = yup
          .string()
          .required("Description is required");
      }

      // Add priority validation if required
      if (showPriorityField && priorityField?.required) {
        baseSchema.priority = yup
          .object()
          .nullable()
          .required("Priority is required");
      }

      if (isPerMisconfiguration) {
        baseSchema.selectedMisconfigurations = yup
          .array()
          .min(1, "At least one misconfiguration must be selected")
          .required("Selected misconfigurations are required");
      }

      // Add custom fields validation schema
      const customFieldsSchema = getCustomFieldsValidationSchema(customFields);

      return yup.object().shape({
        ...baseSchema,
        ...customFieldsSchema,
      });
    }, [
      customFields,
      showSummaryField,
      showDescriptionField,
      isSummaryRequired,
      isDescriptionRequired,
      priorityField,
      jsmIsAgent,
    ]);

    // Expose the form's submit function through the ref
    useImperativeHandle(ref, () => ({
      submitForm: () => formikRef.current?.handleSubmit(),
    }));

    return (
      <Box sx={{ width: JIRA_FORM_WIDTH }}>
        <Formik
          innerRef={formikRef}
          onSubmit={onSubmit}
          initialValues={formInitialValues}
          validationSchema={formValidationSchema}
          validateOnSubmit
        >
          {({ handleSubmit }) => (
            <Form onSubmit={handleSubmit}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <JiraRequestType
                  options={requestTypes}
                  showOptions={requestTypesIsSuccess}
                  selectedOption={selectedRequestType}
                  setSelectedOption={setSelectedRequestType}
                />
                <JiraReporter
                  options={reporterOptions}
                  showOptions={reporterOptionsIsSuccess}
                  jsmIsAgent={jsmIsAgent}
                />
                {showSummaryField && (
                  <JiraSummary required={isSummaryRequired} />
                )}
                {showDescriptionField && (
                  <JiraDescription required={isDescriptionRequired} />
                )}
                <JiraPriority
                  priorityField={priorityField}
                  isLoading={requestFieldsIsLoading}
                />

                {requestTypesIsSuccess && selectedRequestType && (
                  <JiraCustomFields
                    customFields={customFields}
                    isLoading={requestFieldsIsLoading}
                    assigneeOptions={assigneeOptions}
                  />
                )}
                {isPerMisconfiguration ? (
                  <JiraSelectedMisconfigurations fullControl={fullControl} />
                ) : (
                  <JiraIncludeEntities />
                )}
              </Box>
              <Button type="submit" style={{ display: "none" }} />
            </Form>
          )}
        </Formik>
      </Box>
    );
  }
);

export default JSMTicketForm;
